import { fetchIsAdmin } from "@/utils/admin";
import DesktopNotice, { useDesktopWeb } from '@/components/DesktopNotice';
import { setChallengeSimulation } from "@/constants/challenge";
import * as Sentry from '@sentry/react-native';
import { Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold, useFonts } from '@expo-google-fonts/inter';
import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider,
} from "@react-navigation/native";
import { supabase } from "@/utils/supabase";
import Constants from "expo-constants";
import { Stack, useNavigationContainerRef } from "expo-router";
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from "expo-status-bar";
import * as SystemUI from 'expo-system-ui';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import "react-native-reanimated";

import { initI18n } from "@/i18n";
import { initAnalytics, identifyUser, resetUser } from "@/utils/analytics";
import { flushPendingOnboardingRewards } from "@/app/onboarding-wheel";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { isAbortEvent, isExpectedAuthLinkEvent, scrubSentryEvent, scrubString, scrubValue } from "@/utils/sentryScrub";

const routingInstrumentation = Sentry.reactNavigationIntegration();

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enabled: !__DEV__,
  environment: __DEV__ ? 'development' : 'production',
  release: `repas@${Constants.expoConfig?.version ?? '1.0.0'}`,
  dist: Constants.expoConfig?.extra?.eas?.projectId,
  sendDefaultPii: false,
  tracesSampleRate: 0.2,
  integrations: [routingInstrumentation],
  beforeSend(event) {
    if (isAbortEvent(event) || isExpectedAuthLinkEvent(event)) return null;
    return scrubSentryEvent(event);
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data) breadcrumb.data = scrubValue(breadcrumb.data) as typeof breadcrumb.data;
    if (typeof breadcrumb.message === 'string') breadcrumb.message = scrubString(breadcrumb.message);
    return breadcrumb;
  },
});

export const unstable_settings = {
  anchor: "(tabs)",
};

// Prevent splash screen from auto-hiding
SplashScreen.preventAutoHideAsync();

// Fond de fenêtre natif = couleur du splash (#D4874A). Sans ça, Android (et
// parfois iOS) peut peindre une frame BLANCHE entre le masquage du splash et
// le premier rendu React → flash blanc au démarrage. Ce fond n'est visible
// que lorsqu'aucune vue ne recouvre la fenêtre.
SystemUI.setBackgroundColorAsync('#D4874A').catch(() => undefined);

// Thèmes de navigation : le `background` de React Navigation est peint
// derrière chaque écran (visible pendant les transitions et avant le premier
// paint). DefaultTheme est BLANC → flash blanc. Toute l'app est claire
// (fond crème #FBEFE3), on aligne donc les deux thèmes sur cette couleur.
const RepasLightTheme = {
  ...DefaultTheme,
  colors: { ...DefaultTheme.colors, background: '#FBEFE3' },
};
const RepasDarkTheme = {
  ...DarkTheme,
  colors: { ...DarkTheme.colors, background: '#FBEFE3' },
};

function RootLayout() {
  const colorScheme = useColorScheme();
  const navigationRef = useNavigationContainerRef();
  const desktopWeb = useDesktopWeb();

  // WEB UNIQUEMENT : react-native-web rend les TextInput en <input>/<textarea>
  // natifs, qui affichent l'outline de focus par défaut du navigateur (cadre
  // bleu/noir disgracieux). On le neutralise via une feuille de style injectée
  // une seule fois. Ce code ne s'exécute jamais sur iOS/Android.
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    if (document.getElementById("repas-web-input-reset")) return;
    const style = document.createElement("style");
    style.id = "repas-web-input-reset";
    style.textContent = `
      input, textarea, select, [contenteditable] {
        outline: none !important;
        -webkit-tap-highlight-color: transparent;
      }
      input:focus, textarea:focus, select:focus {
        outline: none !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const [loaded, error] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  });

  // i18n : initialisé AVANT le premier rendu (comme les fonts, sous le splash).
  // Instance i18next globale → pas de <I18nextProvider> nécessaire. En cas
  // d'échec improbable, on rend quand même (t() affiche la clé plutôt que de
  // bloquer l'app sur le splash).
  const [i18nReady, setI18nReady] = useState(false);
  useEffect(() => {
    initI18n()
      .catch((e) => Sentry.captureException(e))
      .finally(() => setI18nReady(true));
  }, []);

  const appReady = Boolean((loaded || error) && i18nReady);
  usePushNotifications(appReady);

  // Connect Sentry to navigation for screen tracking
  useEffect(() => {
    if (navigationRef?.current) {
      routingInstrumentation.registerNavigationContainer(navigationRef);
    }
  }, [navigationRef]);

  // Initialize analytics & identify user in Sentry + Amplitude
  useEffect(() => {
    initAnalytics();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
        identifyUser(session.user.id);
        // setTimeout : ne jamais faire d'appel Supabase DANS le callback
        // onAuthStateChange (risque de deadlock sur le verrou auth supabase-js).
        const userId = session.user.id;
        setTimeout(() => {
          // Mode test « semaine de concours » : uniquement pour les admins
          // (profiles.is_admin, lu en base). Un utilisateur normal garde les
          // vraies dates.
          fetchIsAdmin(userId)
            .then(setChallengeSimulation)
            .catch(() => setChallengeSimulation(false));
          // Récompenses de la roue d'onboarding en attente (bonus points +
          // participation au tirage), créditées au premier login. Idempotent.
          flushPendingOnboardingRewards(userId).catch(() => undefined);
        }, 0);
      } else {
        setChallengeSimulation(false);
        Sentry.setUser(null);
        resetUser();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if ((loaded || error) && i18nReady) {
      SplashScreen.hideAsync();
    }
  }, [loaded, error, i18nReady]);

  if ((!loaded && !error) || !i18nReady) {
    return null;
  }

  return (
    <ProfileProvider>
      <ThemeProvider value={colorScheme === "dark" ? RepasDarkTheme : RepasLightTheme}>
        <DesktopNotice visible={desktopWeb} />
        {!desktopWeb && <Stack screenOptions={{ contentStyle: { backgroundColor: '#FBEFE3' } }}>
          <Stack.Screen name="index" options={{ headerShown: false }} />
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
          <Stack.Screen name="onboarding" options={{ headerShown: false, gestureEnabled: false }} />
          <Stack.Screen name="onboarding-wheel" options={{ headerShown: false, gestureEnabled: false }} />
          <Stack.Screen name="login" options={{ headerShown: false, gestureEnabled: false }} />
          <Stack.Screen name="create-account" options={{ headerShown: false, gestureEnabled: false }} />
          <Stack.Screen name="reset-password" options={{ headerShown: false, gestureEnabled: false }} />
          <Stack.Screen name="confirmation" options={{ headerShown: false, gestureEnabled: false }} />
          <Stack.Screen name="notification-permission" options={{ headerShown: false }} />
          <Stack.Screen name="ranking-consent" options={{ headerShown: false }} />
          <Stack.Screen name="settings" options={{ headerShown: false }} />
          <Stack.Screen name="edit-profile" options={{ headerShown: false }} />
          <Stack.Screen name="edit-objectives" options={{ headerShown: false }} />
          <Stack.Screen name="go-further" options={{ headerShown: false }} />
          <Stack.Screen name="privacy-policy" options={{ headerShown: false }} />
          <Stack.Screen name="terms-of-use" options={{ headerShown: false }} />
          <Stack.Screen name="cadeaux" options={{ headerShown: false }} />
          <Stack.Screen name="partenaires" options={{ headerShown: false }} />
        </Stack>}
        <StatusBar style="auto" />
      </ThemeProvider>
    </ProfileProvider>
  );
}

export default Sentry.wrap(RootLayout);
